import { Component, Input, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NumberRange } from '../number-range.model';
import { NumberRangeService } from '../number-range.service';

/**
 * Form control for a number backed by a number range. Shows the stored value and lets the
 * user assign the next number, which atomically increments the range on the backend.
 */
@Component({
  selector: 'app-number-range-input',
  templateUrl: './number-range-input.component.html',
  styleUrls: ['./number-range-input.component.scss'],
  imports: [MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatTooltipModule, MatSnackBarModule],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => NumberRangeInputComponent), multi: true }]
})
export class NumberRangeInputComponent implements ControlValueAccessor, OnChanges {

  @Input() label = '';
  @Input() rangeKey: string | null = null;

  value: number | string | null = null;
  disabled = false;
  busy = false;
  range: NumberRange | null = null;
  rangeError = '';

  private onChange: (value: number | string | null) => void = () => { };
  onTouched: () => void = () => { };

  constructor(private numberRangeService: NumberRangeService, private snackBar: MatSnackBar) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['rangeKey']) {
      this.loadRange();
    }
  }

  get canAssign(): boolean {
    return !!this.range && !this.busy && !this.disabled && this.range.actual < this.range.max;
  }

  get assignTooltip(): string {
    if (!this.rangeKey) {
      return 'No number range available';
    }
    if (!this.range) {
      return this.rangeError || `Loading number range ${this.rangeKey}`;
    }
    if (this.range.actual >= this.range.max) {
      return `Number range ${this.rangeKey} is exhausted`;
    }
    return `Assign next number from ${this.rangeKey} (${this.range.actual + 1})`;
  }

  assignNext() {
    if (!this.canAssign || !this.rangeKey) {
      return;
    }
    const hasValue = this.value !== null && this.value !== undefined && `${this.value}`.trim() !== '';
    if (hasValue && !confirm(`Replace ${this.label} ${this.value} with the next number from ${this.rangeKey}?`)) {
      return;
    }
    this.busy = true;
    this.numberRangeService.increment(this.rangeKey).subscribe({
      next: (range) => {
        this.busy = false;
        this.range = range;
        this.setValue(range.actual);
      },
      error: (err: HttpErrorResponse) => {
        this.busy = false;
        const message = typeof err.error === 'string' && err.error ? err.error : `Incrementing number range ${this.rangeKey} failed.`;
        this.snackBar.open(message, 'Close', { duration: 5000 });
        this.loadRange();
      }
    });
  }

  onInput(event: Event) {
    const raw = (event.target as HTMLInputElement).value.trim();
    this.setValue(raw === '' ? null : (isNaN(Number(raw)) ? raw : Number(raw)));
  }

  writeValue(value: number | string | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: number | string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private setValue(value: number | string | null) {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  private loadRange() {
    this.range = null;
    this.rangeError = '';
    const key = this.rangeKey;
    if (!key) {
      return;
    }
    this.numberRangeService.get(key).subscribe({
      next: (range) => {
        if (key === this.rangeKey) {
          this.range = range;
        }
      },
      error: (err: HttpErrorResponse) => {
        if (key === this.rangeKey) {
          this.rangeError = err.status === 404 ? `Number range ${key} not found` : `Loading number range ${key} failed`;
        }
      }
    });
  }
}
